<script setup lang="ts">
import AppButton from '@/application/components/ui/AppButton.vue';
import { ElAlert } from 'element-plus';
import { NotificationVariant, useNotification } from '@/application/components/ui/useNotification';

import ContractForm from '@/contracts/components/ContractForm.vue';
import type { Contract } from '@/api/contracts/types';
import { formatDate } from '@/application/utils/date';
import { useLicensePlate } from '@/contracts/utils/licensePlate';
import { updateContract } from '@/api/contracts/contractsApi';
import { useContractStore } from '@/contracts/stores/contractStore';
import { useForm } from '@/api/lib/integration.ts';
import { TrackEventCategory } from '@/application/utils/tracking/events.ts';
import { useTagManager } from '@demv_systems/feu-tag-manager';
import { intervalMap } from '@/contracts/utils/labelFormatting.ts';

const props = defineProps<{ contract: Contract }>();
const emit = defineEmits(['close']);

const { fetchContractsAndDepots } = useContractStore();
const { show: showNotification } = useNotification();

const {
  unterscheidungszeichen,
  erkennungszeichen,
  erkennungsnummer,
  abschliessendeszeichen
} = useLicensePlate(props.contract.meta?.kfz?.licensePlate);

const {
  form,
  submit,
  errors,
  updateField,
  errorMsg
} = useForm(
  updateContract,
  {
    data: {
      id: props.contract.id,
      sparte_id: props.contract.product.id || null,
      gesellschaft_id: props.contract.company.id || null,
      vertragsnummer: props.contract.number,
      beginn: props.contract.term?.start ? formatDate(props.contract.term.start) : null,
      ablaufdatum: props.contract.term?.end ? formatDate(props.contract.term.end) : null,
      beitrag: props.contract.payment?.zahlbeitrag || null,
      zahlweise: props.contract.payment?.interval && intervalMap.get(props.contract.payment.interval) || null,
      kennzeichen: {
        unterscheidungszeichen,
        erkennungszeichen,
        erkennungsnummer,
        abschliessendeszeichen,
      },
    },
    previous: {
      id: props.contract.id,
      sparte_id: props.contract.product.id || null,
      gesellschaft_id: props.contract.company.id || null,
      vertragsnummer: props.contract.number,
      beginn: props.contract.term?.start ? formatDate(props.contract.term.start) : null,
      ablaufdatum: props.contract.term?.end ? formatDate(props.contract.term.end) : null,
      beitrag: props.contract.payment?.zahlbeitrag || null,
      zahlweise: props.contract.payment?.interval && intervalMap.get(props.contract.payment.interval) || null,
      kennzeichen: {
        unterscheidungszeichen,
        erkennungszeichen,
        erkennungsnummer,
        abschliessendeszeichen,
      },
    },
  },
  async () => {
    emit('close');
    showNotification('Ihre Daten wurden erfolgreich gespeichert.', NotificationVariant.Success);
    await fetchContractsAndDepots(true);
    useTagManager().push({ event: TrackEventCategory.CONTRACT, action: 'bearbeiten' })
  },
  () => {},
  'data'
);
</script>

<template>
  <div class="flex flex-col h-full justify-between gap-4 pt-5">
    <ContractForm
      :model-value="form.data"
      :validation-errors="errors"
      :update-field="updateField"
      update-form
    />

    <ElAlert
      v-if="errorMsg"
      :title="errorMsg"
      type="error"
      class="my-5"
      show-icon
    />

    <AppButton
      type="primary"
      class="sticky bottom-0 mt-5 sm:ml-auto"
      :handler="submit"
    >
      Speichern
    </AppButton>
  </div>
</template>
